import { useCallback, useEffect, useState } from 'react';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, EmptyState, EndLine, LastUpdate, ListRow, useRowKeys, Waiting } from '@beyond-js/ui/react';
import { Outage, Problem } from '@accounts/ui';

interface Entry {
	seq: number;
	at: string;
	action: string;
	outcome: string;
	device?: string;
	ended?: number;
}

/**
 * The account's recent security activity (ACC-12): sign-ins, password and method changes, ended
 * sessions, each with when and from what device, newest first, older ones on request.
 *
 * When Accounts does not answer, the page says so once (`Outage`, D163): as the page's body while nothing
 * was read, as a line over the entries kept from the last read, which says when that was, and for older
 * entries asked for, in their place at the list's end.
 */
export function Activity() {
	const { text, date, client, renewed, language } = usePresence();
	const [entries, setEntries] = useState<Entry[]>();
	const [next, setNext] = useState<number>();
	// When the newest entries were last read, said over them while a later read does not answer
	const [at, setAt] = useState<number>();
	// Why the newest entries could not be read, and why the older ones asked for could not be
	const [unread, setUnread] = useState<Refusal>();
	const [older, setOlder] = useState<Refusal>();
	const [busy, setBusy] = useState(false);

	/** A page of the activity, newest first: the first one, or the one before `before` */
	const page = useCallback((before?: number) => client.get<{ entries: Entry[]; next?: number }>(`/v1/account/activity${before ? `?before=${before}` : ''}`), [client]);

	// Each resolves whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				const found = await page();
				setEntries(found.entries);
				setNext(found.next);
				setAt(Date.now());
			}, setUnread),
		[page]
	);
	const more = useCallback(async () => {
		setBusy(true);
		try {
			return await Refusal.answered(async () => {
				const found = await page(next);
				setEntries(current => [...(current ?? []), ...found.entries]);
				setNext(found.next);
			}, setOlder);
		} finally {
			setBusy(false);
		}
	}, [page, next]);

	useEffect(() => {
		void read();
	}, [read, renewed]);

	const outage = Boolean(unread?.unreachable);
	// Up and Down move between the entries (L7)
	const keys = useRowKeys();
	return (
		<Screen template="list" width="fluid" title={text('nav.activity')}>
			<Outage
				whole={outage && !entries}
				reads={[
					{ key: 'activity', title: text('nav.activity'), unread, run: read },
					{ key: 'older', title: text('outage.older'), unread: older, run: more }
				]}
			/>
			{unread && !outage ? <Problem error={unread} /> : null}
			{older && !older.unreachable ? <Problem error={older} /> : null}
			{!entries && !unread ? <Waiting language={language} rows={3} /> : null}
			{entries && outage ? <LastUpdate time={at ?? null} locale={language} labels={LastUpdate.labels[language]} /> : null}
			{entries && !entries.length ? <EmptyState title={text('activity.none')} labels={EmptyState.labels[language]} /> : null}
			{/* A history in the family's rows: what happened, a refused attempt marked and said (L2), then when and
			    from where in the row's quiet line */}
			{entries?.length ? (
				<ul ref={keys} className="bui-rows" aria-label={text('nav.activity')}>
					{entries.map(entry => (
						<ListRow
							key={entry.seq}
							title={text(`activity.${entry.action}`)}
							lead={entry.outcome === 'refused' ? { state: 'failed', label: text('activity.refused') } : null}
							meta={[date(entry.at), entry.device, entry.ended ? text('activity.ended', { count: entry.ended }) : null]}
							labels={ListRow.labels[language]}
						/>
					))}
				</ul>
			) : null}
			{/* Older entries that did not answer say so where they would be; the page's notice reads them again */}
			{older?.unreachable ? (
				<EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} />
			) : next ? (
				<Button variant="quiet" busy={busy} onClick={() => void more()}>
					{text('activity.more')}
				</Button>
			) : entries?.length ? (
				// Once nothing is older, the history says it ends (L5)
				<EndLine text={text('audit.end')} labels={EndLine.labels[language]} />
			) : null}
		</Screen>
	);
}
